import { useEffect, useState } from 'react'
import { useLang } from '../lang'
import { useFollow } from '../favourites'
import { useAccount } from '../api'
import { SidePanel } from './SidePanel'
import { Button, Select, Checkbox, SegmentedControl, StateLine } from '../design'
import type { CurrentUser, Lang } from '../types'
import * as a from './account.css'

function csrf(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

// The account side-panel: identity, the site language, the daily-letter toggle, and the followed
// species.
// The follow list is driven entirely by the FollowProvider (the same state the Directory
// checkboxes use) — this panel just resolves those sci_names to names from the picker list.
export function AccountPanel({
  user,
  onClose,
  onOpenAdmin,
}: {
  user: CurrentUser
  onClose: () => void
  onOpenAdmin: () => void
}) {
  const { t, lang, setLang } = useLang()
  const { followed, toggle } = useFollow()
  const { data } = useAccount(true)

  const names = new Map((data?.species ?? []).map((s) => [s.sci, s]))
  const follows = followed
    .map((sci) => names.get(sci) ?? { sci, en: sci, ga: null })
    .sort((a, b) => a.en.localeCompare(b.en))

  // The daily letter (roundup) — a local optimistic mirror of the server state, confirmed
  // or reverted by the toggle's JSON reply so the checkbox never lies about a write.
  const [roundup, setRoundup] = useState(false)
  useEffect(() => {
    if (data) setRoundup(data.roundup)
  }, [data])

  const setLetter = (on: boolean) => {
    setRoundup(on)
    fetch('/subscriptions/cadence', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf() },
      body: JSON.stringify({ alert_type: 'roundup', cadence: on ? 'digest' : 'off' }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => setRoundup(!!j.roundup))
      .catch(() => setRoundup(!on))
  }

  return (
    <SidePanel title={t('Account', 'Cuntas')} onClose={onClose}>
      <section className={a.me}>
        {user.avatar_url ? (
          <img className={a.avatar} src={user.avatar_url} alt={user.name} referrerPolicy="no-referrer" />
        ) : (
          <span className={a.avatarInitial}>{user.name.trim().charAt(0).toUpperCase()}</span>
        )}
        <div className={a.meId}>
          <span className={a.meName}>{user.name}</span>
          {user.email && user.email !== user.name && <span className={a.meEmail}>{user.email}</span>}
        </div>
        {user.admin && (
          <Button variant="quiet" size="sm" className={a.meAdmin} onClick={onOpenAdmin}>
            {t('Admin', 'Riarachán')}
          </Button>
        )}
      </section>

      {/* The masthead's EN|GA is the control most readers use, and it must stay there — it is the
          only one a SIGNED-OUT visitor has. This is the same setting, somewhere a reader would think
          to look. No fetch: setLang writes the ui_lang cookie and flips <html lang> itself. */}
      <section className={a.sec}>
        <h2 className={a.h2}>{t('Language', 'Teanga')}</h2>
        <p className={a.note}>
          {t(
            'The language this site reads in. Kept in this browser.',
            'An teanga a léann an suíomh seo inti. Coinnítear sa bhrabhsálaí seo í.',
          )}
        </p>
        <ul className={a.rules}>
          <li className={a.rule}>
            <span className={a.ruleLabel}>{t('Site language', 'Teanga an tsuímh')}</span>
            <SegmentedControl<Lang>
              options={[
                { value: 'en', label: 'English' },
                { value: 'ga', label: 'Gaeilge' },
              ]}
              value={lang}
              onChange={setLang}
              ariaLabel={t('Site language', 'Teanga an tsuímh')}
            />
          </li>
        </ul>
      </section>

      <section className={a.sec}>
        <h2 className={a.h2}>{t('The daily letter', 'An litir laethúil')}</h2>
        <p className={a.note}>
          {t(
            "The day's journal, by email, just after midnight.",
            'Dialann an lae, ar ríomhphost, díreach tar éis meán oíche.',
          )}
        </p>
        <ul className={a.rules}>
          <li className={a.rule}>
            <span className={a.ruleLabel}>{t('Daily letter', 'Litir laethúil')}</span>
            <Checkbox
              checked={roundup}
              onChange={(e) => setLetter(e.target.checked)}
              aria-label={t('Daily letter', 'Litir laethúil')}
            />
          </li>
        </ul>
      </section>

      <section className={a.sec}>
        <h2 className={a.h2}>{t("Species you're following", 'Speicis a leanann tú')}</h2>
        {follows.length > 0 ? (
          <ul className={a.list}>
            {follows.map((f) => {
              const primary = lang === 'ga' && f.ga ? f.ga : f.en
              const secondary = lang === 'ga' ? f.en : f.ga
              return (
                <li className={a.item} key={f.sci}>
                  <span className={a.name}>
                    {primary}
                    {secondary && secondary !== primary && (
                      <>
                        {' '}
                        <em lang={lang === 'ga' ? 'en' : 'ga'} className={a.ga}>
                          {secondary}
                        </em>
                      </>
                    )}
                  </span>
                  <Button variant="quiet" size="sm" onClick={() => toggle(f.sci)}>
                    {t('Unfollow', 'Ná lean')}
                  </Button>
                </li>
              )
            })}
          </ul>
        ) : (
          <StateLine>
            {t("You're not following any species yet.", 'Níl tú ag leanúint aon speiceas fós.')}
          </StateLine>
        )}
        <div className={a.add}>
          <Select
            value=""
            aria-label={t('Follow a species', 'Lean speiceas')}
            onChange={(e) => e.target.value && toggle(e.target.value)}
          >
            <option value="">{t('Choose a species…', 'Roghnaigh speiceas…')}</option>
            {(data?.species ?? [])
              .filter((s) => !followed.includes(s.sci))
              .map((s) => (
                <option key={s.sci} value={s.sci}>
                  {s.ga ? `${s.en} · ${s.ga}` : s.en}
                </option>
              ))}
          </Select>
        </div>
      </section>

      <footer className={a.foot}>
        <form method="post" action="/logout">
          <input type="hidden" name="_method" value="delete" />
          <input type="hidden" name="authenticity_token" value={csrf()} />
          <Button variant="quiet" size="sm" type="submit">
            {t('Sign out', 'Logáil amach')}
          </Button>
        </form>
      </footer>
    </SidePanel>
  )
}
